import React, { useEffect, useMemo, useState } from "react"
import Autoplay from "embla-carousel-autoplay"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  type CarouselApi,
} from "@love-irish/ui-components"
import { CodexRecord } from "@love-irish/resource-list-item"
import { sectionsFromSenses } from "@love-irish/resource-codex-utils"
import type { CollectionItem } from "@love-irish/types"

// The card reads `item.sections` (the POS-grouped display shape) to show rich metadata; we query the
// normalized `senses` records and derive `sections` here, exactly like the codex index does. Without
// this the card falls back to the bare translation.
const withDerivedSections = (resource: any) =>
  resource?.__typename === "ItemCodexRecord"
    ? {
        ...resource,
        sections: sectionsFromSenses(
          resource.senses ?? [],
          resource.name ?? ""
        ),
      }
    : resource

// Renders a section's collection items straight through the shared resource-list-item CodexRecord —
// the same server shape SectionRenderer consumes (collectionItems[].resource) — so each card gets the
// full metadata (senses/POS, pronunciation, audio) and the resource menu (save / add-to-collection).
const VocabCard: React.FC<{ item: CollectionItem; dark?: boolean }> = ({
  item,
  dark,
}) => {
  return (
    <div
      className={`flex h-full flex-col rounded-xl border p-4 shadow-sm transition-transform hover:-translate-y-1 bg-white/10 border-white/20 backdrop-blur-md`}
    >
      <CodexRecord item={withDerivedSections(item.resource)} />
    </div>
  )
}

export const VocabCarousel: React.FC<{
  collectionItems: readonly CollectionItem[]
  dark?: boolean
  /**
   * Changing this (the active scene / section key) snaps the carousel back to the first card. Without
   * it, switching from a scene you'd scrolled to the end of leaves the next scene scrolled past its
   * opening cards, so you'd miss them.
   */
  resetKey?: string
  /**
   * Cycle through the cards on a timer (the /tv route, or a section configured to autoplay). Pauses on
   * hover and resumes; interaction doesn't kill it, so an ambient display keeps moving.
   */
  autoplay?: boolean
}> = ({ collectionItems, dark, resetKey, autoplay = false }) => {
  const [api, setApi] = useState<CarouselApi | null>(null)

  // Memoize the plugin instance so the carousel doesn't reinitialize every render.
  const plugins = useMemo(
    () =>
      autoplay
        ? [
            Autoplay({
              delay: 6000,
              stopOnInteraction: false,
              stopOnMouseEnter: true,
            }),
          ]
        : undefined,
    [autoplay]
  )

  useEffect(() => {
    api?.scrollTo(0, true) // `true` = jump, no animation, on scene change
  }, [api, resetKey])

  if (!collectionItems.length) return null

  return (
    <Carousel
      setApi={setApi}
      opts={{ align: "start", dragFree: true, loop: autoplay }}
      plugins={plugins}
      className="w-full"
    >
      {collectionItems.length > 2 && (
        <div className="relative flex gap-2 justify-end mb-2 dark">
          <CarouselPrevious className="relative left-0 top-0 transform-none btn-light" />
          <CarouselNext className="relative left-0 top-0 transform-none btn-light" />
        </div>
      )}
      <CarouselContent className="py-3 items-start">
        {collectionItems.map((item) => (
          <CarouselItem
            key={item.id}
            className="basis-full md:basis-1/2 xl:basis-1/3 2xl:basis-1/4"
          >
            <VocabCard item={item} />
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  )
}
